import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { CookieService } from 'ngx-cookie-service';

import { TokenInspection } from './token-inspection.model';

const TOKEN_COOKIE_NAME = 'token';
const ORIGINAL_USER_TOKEN_COOKIE_NAME = 'originalUserToken';
const SESSION_COOKIE_PATH = '/';

/** Legacy paths created when `set()` omitted `path` (browser scoped to current route). */
const LEGACY_SESSION_COOKIE_PATHS = ['/login', '/callback', '/home', '/home/resources'] as const;

@Injectable({ providedIn: 'root' })
export class TokenService {
  private cookieService = inject(CookieService);
  private document = inject(DOCUMENT);

  setToken(token: string | undefined): void {
    if (token) {
      this._setSessionCookie(TOKEN_COOKIE_NAME, token);
    }
  }

  getToken(): string | null {
    return this.cookieService.get(TOKEN_COOKIE_NAME) || null;
  }

  deleteToken(): void {
    this._clearSessionCookie(TOKEN_COOKIE_NAME);
  }

  /**
   * Exposes the session token to sibling subdomains (e.g. legacy report UIs) via a scoped cookie.
   * HttpOnly cannot be set from JavaScript (only via Set-Cookie from the server); we apply Secure
   * when the page is a secure context and SameSite=Lax. See MDN: Document.cookie.
   */
  setTokenCookieForParentSite(parentSiteDomain: string): void {
    const token = this.getToken();
    if (!token) {
      return;
    }
    const parentDomain = this._toParentDomainAttribute(parentSiteDomain);
    this.cookieService.delete(TOKEN_COOKIE_NAME, SESSION_COOKIE_PATH, parentDomain);
    this.cookieService.set(TOKEN_COOKIE_NAME, token, this._sessionCookieOptions(parentDomain));
  }

  setOriginalUserToken(token: string | undefined): void {
    if (token) {
      this._setSessionCookie(ORIGINAL_USER_TOKEN_COOKIE_NAME, token);
    }
  }

  getOriginalUserToken(): string | undefined {
    return this.cookieService.get(ORIGINAL_USER_TOKEN_COOKIE_NAME);
  }

  deleteOriginalUserToken(): void {
    this._clearSessionCookie(ORIGINAL_USER_TOKEN_COOKIE_NAME);
  }

  /**
   * Returns the token expiration, or null when the payload has no `exp` claim.
   * Throws when the token is malformed or cannot be decoded (callers may clear invalid tokens).
   */
  /**
   * Decodes JWT timing claims for auth diagnostics. Never logs the full token.
   */
  inspectToken(token: string | null | undefined): TokenInspection | null {
    const nowMs = Date.now();
    const nowIso = new Date(nowMs).toISOString();

    if (!token) {
      return null;
    }

    const base: Omit<TokenInspection, 'expClaimSeconds' | 'iatClaimSeconds' | 'expiresAtIso' | 'issuedAtIso' | 'expMs' | 'msUntilExpiry' | 'clientConsidersExpired' | 'decodeError'> = {
      tokenSuffix: token.length >= 8 ? token.slice(-8) : token,
      charLength: token.length,
      jwtPartCount: token.split('.').length,
      nowIso,
      nowMs,
    };

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return {
          ...base,
          expClaimSeconds: null,
          iatClaimSeconds: null,
          expiresAtIso: null,
          issuedAtIso: null,
          expMs: null,
          msUntilExpiry: null,
          clientConsidersExpired: true,
          decodeError: `invalid-jwt-parts-${parts.length}`,
        };
      }

      const decoded = JSON.parse(this.decodeBase64Url(parts[1])) as { exp?: number; iat?: number };
      const expClaimSeconds = typeof decoded.exp === 'number' ? decoded.exp : null;
      const iatClaimSeconds = typeof decoded.iat === 'number' ? decoded.iat : null;
      const expMs = expClaimSeconds !== null ? expClaimSeconds * 1000 : null;
      const msUntilExpiry = expMs !== null ? expMs - nowMs : null;
      const clientConsidersExpired = expMs !== null ? nowMs >= expMs : true;

      return {
        ...base,
        expClaimSeconds,
        iatClaimSeconds,
        expiresAtIso: expMs !== null ? new Date(expMs).toISOString() : null,
        issuedAtIso: iatClaimSeconds !== null ? new Date(iatClaimSeconds * 1000).toISOString() : null,
        expMs,
        msUntilExpiry,
        clientConsidersExpired,
        decodeError: null,
      };
    } catch (error) {
      return {
        ...base,
        expClaimSeconds: null,
        iatClaimSeconds: null,
        expiresAtIso: null,
        issuedAtIso: null,
        expMs: null,
        msUntilExpiry: null,
        clientConsidersExpired: true,
        decodeError: error instanceof Error ? error.message : String(error),
      };
    }
  }

  getExpirationDate(token: string): Date | null {
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const decoded = JSON.parse(this.decodeBase64Url(parts[1]));
    if (!decoded.exp) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  private _setSessionCookie(name: string, token: string): void {
    this._clearSessionCookie(name);
    this.cookieService.set(name, token, this._sessionCookieOptions());
  }

  private _clearSessionCookie(name: string): void {
    for (const path of this._pathsToClear()) {
      this.cookieService.delete(name, path);
    }

    const parentDomain = this._parentSiteDomainFromHostname();
    if (parentDomain) {
      this.cookieService.delete(name, SESSION_COOKIE_PATH, parentDomain);
    }

    this.cookieService.delete(name, SESSION_COOKIE_PATH);
    this.cookieService.delete(name);
  }

  private _sessionCookieOptions(domain?: string): {
    path: string;
    domain?: string;
    secure: boolean;
    sameSite: 'Lax';
  } {
    const secure = this.document.defaultView?.isSecureContext === true;

    return {
      path: SESSION_COOKIE_PATH,
      ...(domain ? { domain } : {}),
      secure,
      sameSite: 'Lax',
    };
  }

  private _pathsToClear(): string[] {
    const paths = new Set<string>([SESSION_COOKIE_PATH, ...LEGACY_SESSION_COOKIE_PATHS]);
    const pathname = this.document.defaultView?.location.pathname;

    if (pathname) {
      paths.add(pathname);
      const segments = pathname.split('/').filter(Boolean);
      let prefix = '';
      for (const segment of segments) {
        prefix += `/${segment}`;
        paths.add(prefix);
      }
    }

    return [...paths];
  }

  private _parentSiteDomainFromHostname(): string | null {
    const hostname = this.document.defaultView?.location.hostname;
    if (!hostname) {
      return null;
    }

    const match = /([a-z0-9-]{2,63})\.([a-z.]{2,5})$/i.exec(hostname);
    if (!match) {
      return null;
    }

    return this._toParentDomainAttribute(`${match[1]}.${match[2]}`);
  }

  private _toParentDomainAttribute(parentSiteDomain: string): string {
    return parentSiteDomain.startsWith('.') ? parentSiteDomain : `.${parentSiteDomain}`;
  }

  /**
   * Decodes a JWT segment (header or payload). JWTs use base64url, not standard base64.
   */
  private decodeBase64Url(segment: string): string {
    let base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    switch (base64.length % 4) {
      case 0:
        break;
      case 2:
        base64 += '==';
        break;
      case 3:
        base64 += '=';
        break;
      default:
        throw new Error('Invalid base64url string');
    }
    return atob(base64);
  }
}
